/**
 * 시딩(인플루언서 캠페인) 목록의 숫자 — 캠페인 줄 모양 · 토글 거르기 · 캠페인별 집계 · 목록 합계.
 *
 * 화면(`useCampaignMetricsModel` · `CampaignProvider`)과 서버(`server/mcp/mcp-summary-seeding`)가
 * **같은 함수**를 부른다. 여기 숫자를 바꾸면 화면과 MCP 가 함께 바뀐다 — 한쪽에 사본을 두지 마라.
 *
 * ⚠️ 날짜는 **실행 환경의 로컬 시각**으로 자른다(상태 라벨 · 기간 날짜). 서버가 부를 때는
 * 화면과 같은 시계(KST)로 맞춘 값을 넘겨야 같은 수가 나온다.
 */

import type { FoldedCampaignAccount } from 'src/utils/campaign/campaign-link-budget';
import type { CampaignPeriodFilter } from 'src/utils/campaign/campaign-period-filter';
import type { CampaignDoc, CampaignPost, BrandAccount } from 'src/types/campaign/campaign-domain';

import { fDateDot } from 'src/utils/format/format-time';
import { postInPeriod } from 'src/utils/campaign/campaign-period-filter';
import { getMetricsByScoreTime } from 'src/utils/metrics/post-metric-snapshot';
import { filterPostsByCampaignCriteria } from 'src/utils/moments/view/moment-filter';
import { getCampaignStatusLabelFromPeriod } from 'src/utils/campaign/campaign-period-status';
import { toDateObj, buildCampaignCriteria, filterPostsByPeriodLocal } from 'src/utils/campaign/campaign-criteria';
import { getCampaignExcludedKeySet, excludeCampaignPostsByKeys } from 'src/utils/campaign/campaign-post-exclusion';
import { omitElsSortMetricFields, CAMPAIGN_AGGREGATION_METRICS_OPTS } from 'src/utils/campaign/campaign-metric-utils';

import { campaignTotalBudgetWon } from './campaign-cost';
import { normalizeCampaignAccountName } from './campaign-account-posts';
import { foldForcedLinkBudgetsIntoAccounts } from './campaign-link-budget';
import { readCampaignPostBudgets, readForcedAdditionBudgets } from './campaign-sources';
import { getPostMergeKey, passesCampaignDisplayPostFilters } from './campaign-post-filters';

/**
 * 토글 여섯의 화면 기본값 — 사용자가 탭에서 바꾸기 전의 값.
 * 넷은 켜짐(제외한다), 둘은 꺼짐(`mergeByAccountId` · `includeInstagramStory`). 스토리만 「포함」 쪽 표현이다.
 */
export const CAMPAIGN_TOGGLE_DEFAULTS = Object.freeze({
  excludeOwnAccounts: true,
  excludeDeleted: true,
  mergeByAccountId: false,
  excludeXRepost: true,
  excludeXQuote: true,
  includeInstagramStory: false,
});

/** 토글 다섯 + 기간 — 캠페인별 글을 거르는 조건(합쳐 보기는 셀 때만 쓴다) */
export type CampaignPostToggles = {
  excludeOwnAccounts: boolean;
  excludeDeleted: boolean;
  excludeXRepost: boolean;
  excludeXQuote: boolean;
  includeInstagramStory: boolean;
  periodFilter?: CampaignPeriodFilter | null;
};

/** 캠페인 한 건의 집계 — 강제추가를 병합 키로 합친 뒤 한 번만 센다 */
export type CampaignMetric = {
  campaignId: string;
  /** 회수율의 분자 — 등록한 시딩 계정 중 글이 하나 이상 잡힌 계정 수(`countUploadedSeedingAccounts`) */
  authorCount: number;
  /** 같은 분자를 「같은 계정 합쳐 보기」 기준(계정 이름)으로 센 것 */
  authorCountMerged: number;
  postsCount: number;
  viewTotal: number;
  reactionTotal: number;
  /** 등록 계정 + 링크로 들어온 등록 밖 계정, 링크 예산을 글쓴 계정에 접은 것(`foldForcedLinkBudgetsIntoAccounts`) — 계정별 표·엑셀의 줄 */
  foldedAccounts?: FoldedCampaignAccount[];
  /** 회수율의 분모 — `foldedAccounts` 를 센 것(채널×계정) */
  accountCount?: number;
  /** 같은 분모를 「같은 계정 합쳐 보기」 기준(계정 이름)으로 센 것 */
  accountCountMerged?: number;
  /** 총 예산 — 등록 계정 예산 + 링크 예산(`sumForcedAdditionBudgets` · 아직 안 모인 링크 몫 포함) */
  budgetWon?: number;
};

/** 목록 한 벌의 합계 — 상단 카드와 그룹 줄이 같은 모양을 든다 */
export type CampaignAggregate = {
  activeCount: number;
  totalCount: number;
  postsCount: number;
  totalBudget: number;
  spentBudget: number;
  /** 계정별 예산 합(원) — 표의 「총 예산」 열과 같은 계산 */
  totalBudgetWon: number;
  totalViews: number;
  totalReactions: number;
  /** 회수율(%) 소수 첫째 자리 문자열 — 계정이 없으면 `null` */
  collectionRate: string | null;
  totalAuthorCount: number;
  totalAccountCount: number;
  /** 아직 숫자를 못 받은 캠페인 수 — 범위 「전체」로 받기 전의 종료·예정 캠페인 */
  unloadedCount: number;
  startTime: number | null;
  endTime: number | null;
  markets: string[];
  platforms: string[];
};

/**
 * Firestore 캠페인 문서 → 목록 줄 모양(상태 라벨 · 기간 문자열 · 정렬용 시각).
 * @param now 상태 라벨을 가르는 지금 — 끝나는 날이 없으면 이 날이 끝이다
 */
export function toCampaignListItem(c: CampaignDoc, idx: number, now: Date) {
  const accounts = Array.isArray(c?.accounts) ? c.accounts : [];
  const rawStart = c?.period?.start;
  const rawEnd = c?.period?.end ?? now;
  const sDate = toDateObj(rawStart);
  const eDate = toDateObj(rawEnd) || now;
  const startLabel = sDate ? fDateDot(sDate) : '-';
  const endLabel = eDate ? fDateDot(eDate) : fDateDot(now);
  const statusLabel = getCampaignStatusLabelFromPeriod({
    rawStart,
    rawEnd: c?.period?.end,
    now,
    toDateObj,
  });

  // 마켓·플랫폼은 대소문자를 무시하려고 소문자로 맞춘다
  const marketsRaw = Array.isArray(c?.keywordFilter?.markets)
    ? c.keywordFilter.markets
    : (Array.isArray(c?.market) ? c.market : (Array.isArray(c?.markets) ? c.markets : []));
  const markets = marketsRaw
    .map((m: any) => String(m || '').trim().toLowerCase())
    .filter((m: any) => m.length > 0);

  const platformsRaw = Array.isArray(c?.keywordFilter?.platforms)
    ? c.keywordFilter.platforms
    : (Array.isArray(c?.platforms) ? c.platforms : []);
  const platforms = platformsRaw
    .map((p: any) => String(p || '').trim().toLowerCase())
    .filter((p: any) => p.length > 0);

  return {
    id: c.id || idx,
    title: c.name || c.title || '캠페인',
    status: statusLabel,
    startLabel,
    endLabel,
    startTime: sDate ? sDate.getTime() : Number.MAX_SAFE_INTEGER,
    endTime: eDate ? eDate.getTime() : 0, // 종료일자 기준 정렬용
    color: c.mainColor || '#10A4FF',
    keywords: Array.isArray(c.keywords) ? c.keywords : [],
    accounts,
    accountsCount: accounts.length,
    markets,
    platforms,
    groupId: c?.groupId ? String(c.groupId).trim() : null,
    budget: Number(c?.budget) || 0,
    spentBudget: Number(c?.spentBudget) || 0,
  };
}

/** 목록 줄 한 건 — `toCampaignListItem` 의 결과 */
export type CampaignListItem = ReturnType<typeof toCampaignListItem>;

/**
 * 「같은 계정 합쳐 보기」 기준 계정 수 — 채널이 달라도 계정 이름이 같으면 한 명이다.
 * @param accounts `{ account|username }` 객체 또는 `platform*account` 문자열 목록
 */
export function getAccountCountMerged(accounts: unknown): number {
  if (!Array.isArray(accounts) || accounts.length === 0) return 0;
  const names = new Set(accounts.map(extractAccountName).filter((n) => n.length > 0));
  return names.size;
}

/** 회수율의 분모 — 합쳐 보기면 이름 기준, 아니면 목록 길이 */
export function getCampaignAccountCount(accounts: unknown, merge: unknown): number {
  if (!Array.isArray(accounts) || accounts.length === 0) return 0;
  if (!merge) return accounts.length;
  return getAccountCountMerged(accounts);
}

/**
 * 받아 온 브랜드 글 → 캠페인 조건(키워드·마켓·채널·계정·기간)에 맞는 글. 캠페인 id 마다 한 벌.
 * @param rows 캠페인 문서(`id` 또는 `campaignId`)
 * @param postsByCampaignId 캠페인 id → 받아 온 글
 */
export function filterPostsByCampaignConditions(
  rows: readonly CampaignDoc[],
  postsByCampaignId: Record<string, CampaignPost[]> | null | undefined,
): Record<string, CampaignPost[]> {
  const map = typeof postsByCampaignId === 'object' && postsByCampaignId !== null ? postsByCampaignId : {};
  const list = Array.isArray(rows) ? rows : [];
  const result: Record<string, CampaignPost[]> = {};

  list.forEach((campaign) => {
    const campaignId = String(campaign?.id || campaign?.campaignId || '').trim();
    if (!campaignId) return;

    const basePosts = Array.isArray(map[campaignId]) ? map[campaignId] : [];
    if (basePosts.length === 0) {
      result[campaignId] = [];
      return;
    }

    const campaignCriteria = buildCampaignCriteria(campaign, toDateObj);
    const criteria = {
      keywords: campaignCriteria.keywords,
      markets: campaignCriteria.markets,
      platforms: campaignCriteria.platforms,
      accounts: campaignCriteria.accounts,
    };

    const hasCriteria =
      criteria.keywords.length > 0 ||
      criteria.markets.length > 0 ||
      criteria.platforms.length > 0 ||
      criteria.accounts.length > 0;

    let filteredPosts = hasCriteria
      ? filterPostsByCampaignCriteria(basePosts, criteria, { debug: false, label: '[CampaignContext]' })
      : basePosts;

    filteredPosts = filterPostsByPeriodLocal(filteredPosts, campaignCriteria.period);

    result[campaignId] = filteredPosts;
  });

  return result;
}

/**
 * 캠페인별 글에 토글(자사 계정 · 삭제 · X 리포스트 · X 인용 · 스토리)과 기간(게시일)을 먹인다.
 * 자사 계정은 현재 아이디와 `accountHistory` 를 모두 `채널*아이디` 로 맞춰 뺀다.
 */
export function filterCampaignPostsByToggles(
  postsByCampaignId: Record<string, CampaignPost[]> | null | undefined,
  brandAccounts: readonly BrandAccount[],
  toggles: CampaignPostToggles,
): Record<string, CampaignPost[]> {
  const {
    excludeOwnAccounts,
    excludeDeleted,
    excludeXRepost,
    excludeXQuote,
    includeInstagramStory,
    periodFilter = null,
  } = toggles;
  const map = typeof postsByCampaignId === 'object' && postsByCampaignId !== null
    ? postsByCampaignId
    : {};
  const ownSet =
    excludeOwnAccounts && Array.isArray(brandAccounts) && brandAccounts.length > 0
      ? new Set(
        brandAccounts
          .flatMap((a) => {
            const plat = String(a?.platform || '').toLowerCase()
            const current = String(a?.username || '').trim().toLowerCase()
            const history = Array.isArray(a?.accountHistory)
              ? a.accountHistory.map((h: any) => String(h || '').trim().toLowerCase()).filter(Boolean)
              : []
            return [current, ...history].map((u) => (plat && u ? `${plat}*${u}` : '*'))
          })
          .filter((k) => k && k !== '*')
      )
      : null;

  const next: Record<string, CampaignPost[]> = {};
  Object.entries(map).forEach(([campaignKey, posts]) => {
    const safePosts = Array.isArray(posts) ? posts : [];
    next[campaignKey] = safePosts.filter((p) => {
      if (!postInPeriod(p, periodFilter)) return false;
      if (excludeDeleted && p?.isDeleted === true) return false;
      if (excludeXRepost && p?.isRepost === true) return false;
      if (excludeXQuote && p?.isQuote === true) return false;
      if (!includeInstagramStory && p?.isStory === true) return false;
      if (ownSet) {
        const plat = String(p?.source || p?.platform || '').toLowerCase();
        const acc = String(p?.account || p?.username || '').trim().toLowerCase();
        if (plat && acc && ownSet.has(`${plat}*${acc}`)) return false;
      }
      return true;
    });
  });
  return next;
}

/** 캠페인 id → 거른 글. id 가 비면 빈 배열 */
export function getCampaignPostsById(
  postsByCampaignId: Record<string, CampaignPost[]> | null | undefined,
  campaignKey: unknown,
): CampaignPost[] {
  const map = typeof postsByCampaignId === 'object' && postsByCampaignId !== null
    ? postsByCampaignId
    : {};
  const key = String(campaignKey || '').trim();
  if (!key) return [];
  const posts = map[key];
  return Array.isArray(posts) ? posts : [];
}

/**
 * 캠페인 한 건이 **세는 글 목록** — 자동 수집분에서 제외 키를 빼고, 강제추가분과 병합 키로 합친 뒤 표시 토글로 거른다.
 * 집계(`buildCampaignMetrics`)와 시딩 캠페인 평가의 글 목록이 이 한 벌을 쓴다.
 * ⚠️ 제외 목록(`excludedContentKeys`)은 자동 수집분에만 걸린다 — 강제추가분은 불러올 때 이미 걸렀다.
 * @param posts 토글을 먹인 자동 수집 글(`filterCampaignPostsByToggles`)
 */
export function assembleCampaignFinalPosts({
  campaignRow,
  posts,
  forcedPosts,
  toggles,
}: {
  campaignRow: CampaignDoc | null | undefined;
  posts: readonly CampaignPost[];
  forcedPosts: readonly CampaignPost[];
  toggles: Omit<CampaignPostToggles, 'excludeOwnAccounts'>;
}): CampaignPost[] {
  const { excludeDeleted, excludeXRepost, excludeXQuote, includeInstagramStory, periodFilter = null } = toggles;
  const basePosts = excludeCampaignPostsByKeys([...posts], getCampaignExcludedKeySet(campaignRow));
  const uniqueMap = new Map<string, CampaignPost>();
  [...basePosts, ...forcedPosts].forEach((post) => {
    uniqueMap.set(getPostMergeKey(post), post);
  });
  return Array.from(uniqueMap.values()).filter((post) =>
    passesCampaignDisplayPostFilters(post, {
      excludeDeleted,
      excludeXRepost,
      excludeXQuote,
      includeInstagramStory,
      periodFilter,
    })
  );
}

/**
 * 캠페인별 집계 — 자동 수집분에서 제외 키를 빼고, 강제추가분과 병합 키로 합친 뒤 **한 번만** 센다.
 * ⚠️ 제외 목록(`excludedContentKeys`)은 자동 수집분에만 걸린다 — 강제추가분은 불러올 때 이미 걸렀다.
 * @param postsByCampaignId 토글을 먹인 캠페인별 글(`filterCampaignPostsByToggles`)
 * @param forcedAdditionPostsMap 캠페인 id → 강제추가 글
 */
export function buildCampaignMetrics({
  campaigns,
  campaignRows,
  postsByCampaignId,
  forcedAdditionPostsMap,
  toggles,
}: {
  campaigns: readonly { id: unknown }[];
  campaignRows: readonly CampaignDoc[];
  postsByCampaignId: Record<string, CampaignPost[]>;
  forcedAdditionPostsMap: Record<string, CampaignPost[]>;
  toggles: Omit<CampaignPostToggles, 'excludeOwnAccounts'>;
}): CampaignMetric[] {
  const { excludeDeleted, excludeXRepost, excludeXQuote, includeInstagramStory, periodFilter = null } = toggles;
  const list = Array.isArray(campaigns) ? campaigns : [];
  const rows = Array.isArray(campaignRows) ? campaignRows : [];
  const rowById = new Map();
  rows.forEach((r) => {
    const rid = String(r?.id ?? r?.campaignId ?? '').trim();
    if (rid) rowById.set(rid, r);
  });
  return list.map((c) => {
    const metricCampaignId = String(c.id);

    const campaignRow = rowById.get(metricCampaignId);
    const forcedPosts = Array.isArray(forcedAdditionPostsMap[metricCampaignId])
      ? forcedAdditionPostsMap[metricCampaignId]
      : [];
    const finalPosts = assembleCampaignFinalPosts({
      campaignRow,
      posts: getCampaignPostsById(postsByCampaignId, metricCampaignId),
      forcedPosts,
      toggles: { excludeDeleted, excludeXRepost, excludeXQuote, includeInstagramStory, periodFilter },
    });

    const registeredAccounts = campaignRow ? buildCampaignCriteria(campaignRow).accounts : [];
    // 링크 예산을 계정별로 접는다 — 계정 수(회수율 분모)와 총 예산이 이것을 본다.
    // 🔑 분자도 접은 계정으로 센다 — 링크로 들어온 등록 밖 계정이 분모에 들면 그 글도 분자에 든다
    const foldedAccounts = foldForcedLinkBudgetsIntoAccounts(
      registeredAccounts,
      forcedPosts,
      readForcedAdditionBudgets(campaignRow),
      { posts: finalPosts, budgetsByLink: readCampaignPostBudgets(campaignRow) },
    );
    return {
      campaignId: metricCampaignId,
      ...summarizeCampaignPosts(finalPosts, foldedAccounts),
      foldedAccounts,
      accountCount: getCampaignAccountCount(foldedAccounts, false),
      accountCountMerged: getCampaignAccountCount(foldedAccounts, true),
      budgetWon: campaignTotalBudgetWon(campaignRow, registeredAccounts),
    };
  });
}

/**
 * 거른 글 한 벌 → 회수율 분자 · 포스팅 · 조회 · 반응. 캠페인 집계와 상세 하이라이트(검색을 먹인 글)가 같이 쓴다.
 * 🔑 분자는 `countUploadedSeedingAccounts` 하나로 센다 — 캠페인 계정(등록 + 링크로 들어온 계정) 밖의 글(태그 계정·계정 미확인)은 포스팅·조회·반응에는 들고 분자에는 안 든다.
 * @param registeredAccounts 캠페인 계정 — 링크로 들어온 계정까지 접은 줄(`foldForcedLinkBudgetsIntoAccounts`), 접기 전이면 등록 시딩 계정 — 비면 분자 0
 */
export function summarizeCampaignPosts(
  posts: readonly CampaignPost[],
  registeredAccounts: unknown = [],
): Omit<CampaignMetric, 'campaignId'> {
  let viewTotal = 0;
  let reactionTotal = 0;
  const uploaded = countUploadedSeedingAccounts(posts, registeredAccounts);

  (Array.isArray(posts) ? posts : []).forEach((post) => {
    const postMetrics = getMetricsByScoreTime(
      omitElsSortMetricFields(post),
      CAMPAIGN_AGGREGATION_METRICS_OPTS,
    );
    const view = Number(
      postMetrics?.viewCount ||
      post?.viewCount ||
      post?.views ||
      post?.playCount ||
      post?.impressions ||
      0
    );
    if (Number.isFinite(view)) viewTotal += view;

    const parts = reactionPartsOf(postMetrics);
    const reaction = parts.like + parts.comment + parts.saved + parts.share + parts.download;
    if (Number.isFinite(reaction)) reactionTotal += reaction;
  });

  return {
    authorCount: uploaded.count,
    authorCountMerged: uploaded.countMerged,
    postsCount: Array.isArray(posts) ? posts.length : 0,
    viewTotal: Number(viewTotal || 0),
    reactionTotal: Number(reactionTotal || 0),
  };
}

/** 「총 반응」의 지표별 몫 — 키는 캡처 표식 `data-metric` 과 같다 */
export type CampaignReactionParts = { like: number; comment: number; saved: number; share: number; download: number };

/**
 * 거른 글 한 벌의 「총 반응」을 지표별로 — 다섯을 더하면 `summarizeCampaignPosts` 의 `reactionTotal` 과 같다(같은 글 지표를 읽는다).
 * 메일 캡처가 숨긴 지표를 빼고 다시 더하도록 하이라이트 카드가 표식으로 싣는다.
 */
export function summarizeCampaignReactionParts(posts: readonly CampaignPost[]): CampaignReactionParts {
  const total: CampaignReactionParts = { like: 0, comment: 0, saved: 0, share: 0, download: 0 };
  (Array.isArray(posts) ? posts : []).forEach((post) => {
    const parts = reactionPartsOf(getMetricsByScoreTime(omitElsSortMetricFields(post), CAMPAIGN_AGGREGATION_METRICS_OPTS));
    if (!Number.isFinite(parts.like + parts.comment + parts.saved + parts.share + parts.download)) return;
    (Object.keys(total) as (keyof CampaignReactionParts)[]).forEach((key) => { total[key] += parts[key]; });
  });
  return total;
}

/** 글 하나의 반응 지표 다섯(조회 제외) */
function reactionPartsOf(postMetrics: Record<string, unknown> | null | undefined): CampaignReactionParts {
  return {
    like: Number(postMetrics?.likeCount || 0),
    comment: Number(postMetrics?.commentCount || 0),
    saved: Number(postMetrics?.savedCount || 0),
    share: Number(postMetrics?.shareCount || 0),
    download: Number(postMetrics?.downloadCount || 0),
  };
}

/**
 * 회수율의 분자 — **등록한 시딩 계정 중 글이 하나 이상 잡힌 서로 다른 계정 수**. 그래서 회수율은 100% 를 넘지 않는다.
 *
 * 등록 계정 한 줄 = 한 칸이다. `채널*계정`(객체의 `platform|source` + `account|username`, 또는 그 꼴의 문자열)이면
 * 같은 채널·같은 계정의 글만 맞고, 채널 없이 이름만 적힌 줄은 어느 채널이든 이름이 같은 글이 맞는다.
 * 합쳐 보기(`countMerged`)는 채널을 버리고 계정 이름만 센다 — 분모 `getAccountCountMerged` 와 같은 기준이다.
 * 계정 이름은 대소문자·앞 `@` 하나를 무시한다(`normalizeCampaignAccountName`).
 * @returns count(분모 `accounts.length` 와 짝) · countMerged(분모 `getAccountCountMerged` 와 짝)
 */
export function countUploadedSeedingAccounts(
  posts: readonly CampaignPost[],
  registeredAccounts: unknown,
): { count: number; countMerged: number } {
  const entries = (Array.isArray(registeredAccounts) ? registeredAccounts : [])
    .map(toRegisteredAccountEntry)
    .filter((e): e is { platform: string; name: string } => e !== null);
  if (entries.length === 0) return { count: 0, countMerged: 0 };

  const byKey = new Set(entries.filter((e) => e.platform).map((e) => `${e.platform}*${e.name}`));
  const nameOnly = new Set(entries.filter((e) => !e.platform).map((e) => e.name));
  const allNames = new Set(entries.map((e) => e.name));

  const hit = new Set<string>();
  const hitMerged = new Set<string>();
  (Array.isArray(posts) ? posts : []).forEach((post) => {
    const plat = String(post?.source || post?.platform || '').trim().toLowerCase();
    const name = normalizeAccountName(post?.account || post?.username);
    if (!name) return;
    if (plat && byKey.has(`${plat}*${name}`)) hit.add(`${plat}*${name}`);
    else if (nameOnly.has(name)) hit.add(`*${name}`);
    if (allNames.has(name)) hitMerged.add(name);
  });
  return { count: hit.size, countMerged: hitMerged.size };
}

/**
 * 회수율(%) — 등록 계정 중 올린 계정 ÷ 등록 계정, 소수 첫째 자리 문자열.
 * @returns 등록 계정이 없으면 `null`(화면은 「-」)
 */
export function toCampaignCollectionRate(authorCount: number, accountCount: number): string | null {
  return accountCount > 0 ? ((authorCount / accountCount) * 100).toFixed(1) : null;
}

/** 캠페인 id → 집계 */
export function indexCampaignMetrics(metrics: readonly CampaignMetric[]): Map<string, CampaignMetric> {
  const next = new Map<string, CampaignMetric>();
  (Array.isArray(metrics) ? metrics : []).forEach((m) => {
    const id = String(m.campaignId ?? '').trim();
    if (id) next.set(id, m);
  });
  return next;
}

/** 캠페인 id(`id` → `campaignId`) → 원본 문서 */
export function indexCampaignRows(rows: readonly CampaignDoc[]): Map<string, CampaignDoc> {
  const next = new Map<string, CampaignDoc>();
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    const rid = String(r?.id ?? r?.campaignId ?? '').trim();
    if (rid) next.set(rid, r);
  });
  return next;
}

/**
 * 목록 한 벌의 합계 — 상단 카드 · 그룹 줄 · MCP 시딩 요약이 같은 함수를 쓴다.
 * ⚠️ 포스팅·조회·반응·회수율은 `isIncluded` 가 참인 캠페인만 합산한다(표에 숫자가 보이는 줄).
 *    거짓이면 `unloadedCount` 로만 센다. 캠페인 수·예산은 그 조건 밖이다.
 * @param list 목록 줄(`toCampaignListItem`)
 * @param context.isIncluded 표에 숫자가 보이는 줄인가(`shouldIncludeCampaignMetricsInListTable`)
 */
export function aggregateCampaignList(
  list: readonly any[],
  context: {
    campaignRowById: ReadonlyMap<string, CampaignDoc>;
    campaignsMetricsById: ReadonlyMap<string, CampaignMetric>;
    mergeByAccountId: boolean;
    isIncluded: (campaign: any, campaignId: string) => boolean;
  },
): CampaignAggregate {
  const { campaignRowById, campaignsMetricsById, mergeByAccountId, isIncluded } = context;
  const activeCount = list.filter((c) => c.status === '진행중').length;
  const totalCount = list.length;
  let postsCount = 0;
  let totalViews = 0;
  let totalReactions = 0;
  let totalBudget = 0;
  let spentBudget = 0;
  let totalBudgetWon = 0;
  let totalAuthorCount = 0;
  let totalAccountCount = 0;
  let unloadedCount = 0;
  let startTime = Number.MAX_SAFE_INTEGER;
  let endTime = 0;
  const markets = new Set<string>();
  const platforms = new Set<string>();

  list.forEach((c) => {
    const cid = String(c?.id ?? '').trim();
    totalBudget += Number(c?.budget) || 0;
    spentBudget += Number(c?.spentBudget) || 0;
    (Array.isArray(c?.markets) ? c.markets : []).forEach((m: string) => markets.add(m));
    (Array.isArray(c?.platforms) ? c.platforms : []).forEach((pf: string) => platforms.add(pf));
    const st = Number(c?.startTime);
    if (Number.isFinite(st) && st < startTime) startTime = st;
    const et = Number(c?.endTime);
    if (Number.isFinite(et) && et > endTime) endTime = et;

    const original = cid ? campaignRowById.get(cid) : null;
    const criteria = original ? buildCampaignCriteria(original) : null;
    const m = cid ? campaignsMetricsById.get(cid) : null;
    // 지표가 있으면 그 값 — 없어도 같은 함수(등록 계정 + 링크 예산)로 센다
    totalBudgetWon += typeof m?.budgetWon === 'number' ? m.budgetWon : campaignTotalBudgetWon(original, criteria?.accounts);

    if (!isIncluded(c, cid)) {
      unloadedCount += 1;
      return;
    }
    totalAccountCount += typeof m?.accountCount === 'number'
      ? (mergeByAccountId ? (m.accountCountMerged ?? m.accountCount) : m.accountCount)
      : getCampaignAccountCount(criteria?.accounts, mergeByAccountId);
    if (!m) return;
    postsCount += m.postsCount || 0;
    totalViews += m.viewTotal || 0;
    totalReactions += m.reactionTotal || 0;
    totalAuthorCount += mergeByAccountId ? (m.authorCountMerged || 0) : (m.authorCount || 0);
  });

  const collectionRate = toCampaignCollectionRate(totalAuthorCount, totalAccountCount);

  return {
    activeCount,
    totalCount,
    postsCount,
    totalBudget,
    spentBudget,
    totalBudgetWon,
    totalViews,
    totalReactions,
    collectionRate,
    totalAuthorCount,
    totalAccountCount,
    unloadedCount,
    startTime: startTime === Number.MAX_SAFE_INTEGER ? null : startTime,
    endTime: endTime === 0 ? null : endTime,
    markets: Array.from(markets),
    platforms: Array.from(platforms),
  };
}

// ---- 비공개 ----------------------------------------------------------------

/** 대소문자 · 앞 `@` 하나를 무시한다 — 계정 줄 짝짓기(`campaign-account-posts`)와 같은 규칙 */
function normalizeAccountName(value: unknown): string {
  return normalizeCampaignAccountName(value);
}

/** 등록 계정 한 줄 → 채널(없으면 '') · 이름. 이름이 비면 `null` */
function toRegisteredAccountEntry(acc: unknown): { platform: string; name: string } | null {
  if (typeof acc === 'object' && acc !== null) {
    const row = acc as { platform?: unknown; source?: unknown; account?: unknown; username?: unknown };
    const name = normalizeAccountName(row.account || row.username);
    return name ? { platform: String(row.platform || row.source || '').trim().toLowerCase(), name } : null;
  }
  const str = String(acc || '').trim();
  const starIdx = str.indexOf('*');
  const name = normalizeAccountName(starIdx >= 0 ? str.slice(starIdx + 1) : str);
  if (!name) return null;
  return { platform: starIdx >= 0 ? str.slice(0, starIdx).trim().toLowerCase() : '', name };
}

function extractAccountName(acc: unknown): string {
  return toRegisteredAccountEntry(acc)?.name ?? '';
}
